import { useEffect, useState } from "react";
import { API, backgroundMusicTracks, errMsg, fmtDate, imgSrc, normalizeBusinessSettings, prepareBusinessSettings, uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, KeyRound, Music2, Plus, Trash2 } from "lucide-react";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [pw, setPw] = useState({ old_password: "", new_password: "" });
  const [access, setAccess] = useState({ password: "", access_code: "", confirm_code: "" });
  const [newUser, setNewUser] = useState(null);
  const [musicUploading, setMusicUploading] = useState(false);

  const EMPTY_USER = { name: "", username: "", phone: "", email: "", password: "", access_code: "", role: "customer" };

  const createUser = async () => {
    try {
      await API.post("/users", newUser);
      toast.success(`Akun ${newUser.role === "owner" ? "Pemilik" : "Pelanggan"} berhasil dibuat`);
      setNewUser(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const load = () => {
    API.get("/settings").then((r) => setSettings(normalizeBusinessSettings(r.data))).catch(() => {});
    API.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const payload = prepareBusinessSettings(settings);
      await API.put("/settings", payload);
      setSettings(payload);
      window.dispatchEvent(new Event("nawadah:settings-updated"));
      toast.success("Pengaturan disimpan");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    try {
      await API.put("/auth/password", pw);
      window.location.assign("/masuk?passwordChanged=1");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveAccessCode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(access.access_code)) return toast.error("Kode akses harus tepat 6 digit angka");
    if (access.access_code !== access.confirm_code) return toast.error("Konfirmasi kode akses tidak sama");
    try {
      await API.put("/auth/access-code", { password: access.password, access_code: access.access_code });
      toast.success("Kode akses pemilik berhasil diaktifkan");
      setAccess({ password: "", access_code: "", confirm_code: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm("Hapus akun ini?")) return;
    try {
      await API.delete(`/users/${id}`);
      toast.success("Akun dihapus");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const resetUserPassword = async () => {
    try {
      await API.put(`/users/${resetUser.id}/password`, { new_password: resetPw });
      toast.success(`Kata sandi ${resetUser.name} berhasil direset`);
      setResetUser(null);
      setResetPw("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const uploadBackgroundMusic = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (files.some((file) => !file.type.startsWith("audio/"))) return toast.error("Semua file harus berupa audio MP3, M4A, WAV, AAC, atau OGG");
    if (files.some((file) => file.size > 20 * 1024 * 1024)) return toast.error("Ukuran setiap audio maksimal 20MB");
    setMusicUploading(true);
    try {
      const results = await Promise.allSettled(files.map(async (file, index) => ({
        id: globalThis.crypto?.randomUUID?.() || `music-${Date.now()}-${index}`,
        title: file.name.replace(/\.[^.]+$/, ""),
        url: await uploadFile(file),
      })));
      const uploaded = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
      if (!uploaded.length) throw results.find((result) => result.status === "rejected")?.reason || new Error("Upload audio gagal");
      setSettings((current) => ({
        ...current,
        background_music_playlist: [...backgroundMusicTracks(current), ...uploaded],
        background_music_url: backgroundMusicTracks(current)[0]?.url || uploaded[0].url,
        background_music_title: backgroundMusicTracks(current)[0]?.title || uploaded[0].title,
        background_music_enabled: true,
        background_music_autoplay: true,
        background_music_loop: true,
        background_music_volume: current.background_music_volume ?? 24,
      }));
      const failedCount = results.length - uploaded.length;
      if (failedCount) toast.warning(`${uploaded.length} lagu berhasil diunggah, ${failedCount} gagal.`);
      else toast.success(`${uploaded.length} lagu berhasil ditambahkan. Tekan Simpan Pengaturan untuk mengaktifkannya.`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setMusicUploading(false);
    }
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";

  if (!settings) return <p className="text-slate-500">Memuat...</p>;

  const banks = settings.bank_accounts || [];
  const whatsapps = settings.whatsapp_numbers || [];
  const hours = settings.operating_hours_list || [];
  const musicTracks = backgroundMusicTracks(settings);

  const updateMusicTracks = (updater) => {
    setSettings((current) => {
      const tracks = updater(backgroundMusicTracks(current));
      return {
        ...current,
        background_music_playlist: tracks,
        background_music_url: tracks[0]?.url || "",
        background_music_title: tracks[0]?.title || "",
        background_music_enabled: tracks.length ? current.background_music_enabled !== false : false,
      };
    });
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="admin-settings">
      <div>
        <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Pengaturan</h1>
        <p className="text-sm text-slate-500">Informasi pembayaran, kontak bisnis, akun, dan keamanan</p>
      </div>

      <form onSubmit={saveSettings} className="bg-white border border-slate-200 rounded-md p-6 space-y-5" data-testid="settings-form">
        <h2 className="font-bold">Rekening Pembayaran &amp; Kontak Bisnis</h2>
        <p className="text-xs text-slate-400 -mt-3">Semua daftar di bawah dapat ditambah lebih dari satu. Data ini otomatis dipakai pada pembayaran, kontak website, dan dokumen bisnis.</p>

        <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="settings-bank-list">
          <div className="flex items-center justify-between gap-3">
            <label className={labelCls + " mb-0"}>Rekening Pembayaran</label>
            <button type="button" onClick={() => setSettings({ ...settings, bank_accounts: [...banks, { bank_name: "", account_number: "", holder: "" }] })} data-testid="settings-bank-add" className="inline-flex items-center gap-1 text-xs font-bold text-[#C9A227]"><Plus className="h-3.5 w-3.5" /> Tambah Rekening</button>
          </div>
          {banks.map((bank, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_1.2fr_1fr_auto]">
              <input value={bank.bank_name || ""} onChange={(event) => { const items = [...banks]; items[index] = { ...bank, bank_name: event.target.value }; setSettings({ ...settings, bank_accounts: items }); }} data-testid={`settings-bank-name-${index}`} className={inputCls} placeholder="Nama bank" />
              <input value={bank.account_number || ""} onChange={(event) => { const items = [...banks]; items[index] = { ...bank, account_number: event.target.value }; setSettings({ ...settings, bank_accounts: items }); }} data-testid={`settings-bank-number-${index}`} className={inputCls} placeholder="No. rekening" />
              <input value={bank.holder || ""} onChange={(event) => { const items = [...banks]; items[index] = { ...bank, holder: event.target.value }; setSettings({ ...settings, bank_accounts: items }); }} data-testid={`settings-bank-holder-${index}`} className={inputCls} placeholder="Atas nama" />
              <button type="button" onClick={() => setSettings({ ...settings, bank_accounts: banks.filter((_, itemIndex) => itemIndex !== index) })} aria-label={`Hapus rekening ${index + 1}`} className="p-2 text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!banks.length && <p className="text-xs text-slate-400">Belum ada rekening. Tekan Tambah Rekening.</p>}
        </div>

        <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="settings-wa-list">
          <div className="flex items-center justify-between gap-3">
            <label className={labelCls + " mb-0"}>Kontak WhatsApp</label>
            <button type="button" onClick={() => setSettings({ ...settings, whatsapp_numbers: [...whatsapps, { label: `Admin Nawadah ${whatsapps.length + 1}`, number: "", image: "" }] })} data-testid="settings-wa-add" className="inline-flex items-center gap-1 text-xs font-bold text-[#C9A227]"><Plus className="h-3.5 w-3.5" /> Tambah WhatsApp</button>
          </div>
          {whatsapps.map((whatsapp, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_1.2fr_auto]">
              <input value={whatsapp.label || ""} onChange={(event) => { const items = [...whatsapps]; items[index] = { ...whatsapp, label: event.target.value }; setSettings({ ...settings, whatsapp_numbers: items }); }} data-testid={`settings-wa-label-${index}`} className={inputCls} placeholder="Nama admin" />
              <input value={whatsapp.number || ""} onChange={(event) => { const items = [...whatsapps]; items[index] = { ...whatsapp, number: event.target.value }; setSettings({ ...settings, whatsapp_numbers: items }); }} data-testid={`settings-wa-number-${index}`} className={inputCls} placeholder="08xx-xxxx-xxxx" />
              <button type="button" onClick={() => setSettings({ ...settings, whatsapp_numbers: whatsapps.filter((_, itemIndex) => itemIndex !== index) })} aria-label={`Hapus WhatsApp ${index + 1}`} className="p-2 text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
          <div>
            <label className={labelCls}>Instagram</label>
            <input value={settings.instagram || ""} onChange={(e) => setSettings({ ...settings, instagram: e.target.value })} data-testid="set-ig" className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Alamat Palembang</label>
            <input value={settings.address_palembang || ""} onChange={(e) => setSettings({ ...settings, address_palembang: e.target.value })} data-testid="set-addr-plg" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Alamat Jakarta</label>
            <input value={settings.address_jakarta || ""} onChange={(e) => setSettings({ ...settings, address_jakarta: e.target.value })} data-testid="set-addr-jkt" className={inputCls} />
          </div>
        </div>
        <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="settings-hours-list">
          <div className="flex items-center justify-between gap-3">
            <label className={labelCls + " mb-0"}>Jam Operasional</label>
            <button type="button" onClick={() => setSettings({ ...settings, operating_hours_list: [...hours, { label: "", hours: "" }] })} data-testid="settings-hours-add" className="inline-flex items-center gap-1 text-xs font-bold text-[#C9A227]"><Plus className="h-3.5 w-3.5" /> Tambah Jam</button>
          </div>
          {hours.map((item, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_1.5fr_auto]">
              <input value={item.label || ""} onChange={(event) => { const items = [...hours]; items[index] = { ...item, label: event.target.value }; setSettings({ ...settings, operating_hours_list: items }); }} data-testid={`settings-hours-label-${index}`} className={inputCls} placeholder="Label, mis. Senin–Sabtu" />
              <input value={item.hours || ""} onChange={(event) => { const items = [...hours]; items[index] = { ...item, hours: event.target.value }; setSettings({ ...settings, operating_hours_list: items }); }} data-testid={`settings-hours-value-${index}`} className={inputCls} placeholder="08.00–16.00 WIB" />
              <button type="button" onClick={() => setSettings({ ...settings, operating_hours_list: hours.filter((_, itemIndex) => itemIndex !== index) })} aria-label={`Hapus jam ${index + 1}`} className="p-2 text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="space-y-4 border-t border-slate-100 pt-5" data-testid="settings-background-music">
          <div>
            <h3 className="font-bold text-[#0B1F3A]">Playlist Musik Latar Website</h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Tambahkan satu atau beberapa lagu. Lagu diputar berurutan tanpa tombol mengambang, berhenti saat pengunjung memutar video bersuara, lalu lanjut kembali setelah video dijeda atau selesai.
            </p>
          </div>
          <label className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-md bg-[#0B1F3A] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#102d52] sm:w-fit">
            <Plus className="h-4 w-4" /> {musicUploading ? "Mengunggah lagu…" : "Tambah Lagu"}
            <input
              type="file"
              multiple
              accept="audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav,audio/ogg,audio/aac"
              onChange={uploadBackgroundMusic}
              disabled={musicUploading}
              className="sr-only"
              data-testid="settings-music-upload"
            />
          </label>
          {musicTracks.length ? (
            <div className="space-y-4">
              <div className="space-y-3" data-testid="settings-music-playlist">
                {musicTracks.map((track, index) => (
                  <div key={track.id} className="rounded-md border border-slate-200 bg-slate-50 p-4" data-testid={`settings-music-track-${index}`}>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2 text-sm font-bold text-[#0B1F3A]">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#C9A227]/15 text-[#9B7915]"><Music2 className="h-4 w-4" /></span>
                        <span>Lagu {index + 1}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" disabled={index === 0} onClick={() => updateMusicTracks((tracks) => { const next = [...tracks]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next; })} aria-label={`Naikkan lagu ${index + 1}`} className="rounded p-1.5 text-slate-500 hover:bg-white disabled:cursor-not-allowed disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                        <button type="button" disabled={index === musicTracks.length - 1} onClick={() => updateMusicTracks((tracks) => { const next = [...tracks]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next; })} aria-label={`Turunkan lagu ${index + 1}`} className="rounded p-1.5 text-slate-500 hover:bg-white disabled:cursor-not-allowed disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                        <button type="button" onClick={() => updateMusicTracks((tracks) => tracks.filter((_, trackIndex) => trackIndex !== index))} aria-label={`Hapus lagu ${index + 1}`} className="rounded p-1.5 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <label className={labelCls}>Judul lagu</label>
                    <input value={track.title} onChange={(event) => updateMusicTracks((tracks) => tracks.map((item, trackIndex) => trackIndex === index ? { ...item, title: event.target.value } : item))} className={inputCls} placeholder="Judul lagu" data-testid={`settings-music-title-${index}`} />
                    <audio controls preload="metadata" src={imgSrc(track.url)} className="mt-3 h-11 w-full" data-testid={`settings-music-preview-${index}`} />
                  </div>
                ))}
              </div>
              <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50 p-4">
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label className={labelCls + " mb-0"}>Volume awal</label>
                  <span className="text-xs font-bold text-slate-600">{settings.background_music_volume ?? 24}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={settings.background_music_volume ?? 24}
                  onChange={(event) => setSettings({ ...settings, background_music_volume: Number(event.target.value) })}
                  className="w-full accent-[#C9A227]"
                  data-testid="settings-music-volume"
                />
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-700">
                {[
                  ["background_music_enabled", "Aktifkan di website"],
                  ["background_music_autoplay", "Putar otomatis"],
                  ["background_music_loop", "Ulangi playlist"],
                ].map(([key, label]) => (
                  <label key={key} className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings[key] !== false}
                      onChange={(event) => setSettings({ ...settings, [key]: event.target.checked })}
                      className="h-4 w-4 accent-[#C9A227]"
                    />
                    {label}
                  </label>
                ))}
              </div>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-slate-300 px-4 py-6 text-center text-xs text-slate-500">
              Belum ada lagu. Format: MP3, M4A, WAV, AAC, atau OGG — maksimal 20MB per lagu.
            </div>
          )}
        </div>
        <button type="submit" data-testid="save-settings" className="px-6 py-3 bg-[#0B1F3A] bg-navy-hover text-white font-bold text-sm rounded-md transition-colors">
          Simpan Pengaturan
        </button>
      </form>

      <form onSubmit={savePassword} className="bg-white border border-slate-200 rounded-md p-6 space-y-5" data-testid="owner-password-form">
        <h2 className="font-bold">Keamanan Akun Pemilik</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Kata Sandi Lama</label>
            <input required type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} data-testid="owner-pw-old" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Kata Sandi Baru</label>
            <input required type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="owner-pw-new" className={inputCls} />
          </div>
        </div>
        <button type="submit" data-testid="owner-pw-save" className="px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors">
          Ubah Kata Sandi
        </button>
      </form>

      <form onSubmit={saveAccessCode} className="bg-white border border-amber-200 rounded-md p-6 space-y-5" data-testid="owner-access-code-form">
        <div>
          <h2 className="font-bold">Kode Akses Tambahan Pemilik</h2>
          <p className="mt-1 text-xs text-slate-500">Setelah diaktifkan, akun pemilik wajib memasukkan kode 6 digit ini setiap kali login. Kode dapat diganti dari formulir ini.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Kata Sandi Akun</label>
            <input required type="password" value={access.password} onChange={(e) => setAccess({ ...access, password: e.target.value })} data-testid="access-password" className={inputCls} autoComplete="current-password" />
          </div>
          <div>
            <label className={labelCls}>Kode Baru (6 Digit)</label>
            <input required type="password" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={access.access_code} onChange={(e) => setAccess({ ...access, access_code: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid="access-code-new" className={inputCls} autoComplete="new-password" />
          </div>
          <div>
            <label className={labelCls}>Ulangi Kode</label>
            <input required type="password" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={access.confirm_code} onChange={(e) => setAccess({ ...access, confirm_code: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid="access-code-confirm" className={inputCls} autoComplete="new-password" />
          </div>
        </div>
        <button type="submit" data-testid="owner-access-code-save" className="px-6 py-3 bg-[#0B1F3A] bg-navy-hover text-white font-bold text-sm rounded-md transition-colors">Aktifkan / Ganti Kode Akses</button>
      </form>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="users-management">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-bold">Manajemen Akun</h2>
            <p className="text-xs text-slate-400">Buat akun Pelanggan/Pemilik, reset kata sandi pengguna yang lupa, atau hapus akun yang tidak diperlukan. Akun Anda sendiri tidak dapat dihapus.</p>
          </div>
          <button
            onClick={() => setNewUser({ ...EMPTY_USER })}
            data-testid="add-account"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-xs font-bold rounded-md transition-colors"
          >
            + Tambah Akun
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {users.map((u) => (
            <div key={u.id} className="px-6 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">
                  {u.name} <span className="text-slate-400 font-normal">@{u.username}</span>
                </p>
                <p className="text-xs text-slate-400">
                  {u.phone} • {u.role === "owner" ? `Pemilik • PIN ${u.access_code_enabled ? "aktif" : "belum aktif"}` : "Pelanggan"} • {fmtDate(u.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => { setResetUser(u); setResetPw(""); }} data-testid={`reset-pw-${u.id}`} title="Reset kata sandi" className="p-1.5 text-slate-400 hover:text-[#0B1F3A]">
                  <KeyRound className="h-4 w-4" />
                </button>
                <button onClick={() => removeUser(u.id)} data-testid={`delete-user-${u.id}`} className="p-1.5 text-slate-400 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setResetUser(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-md p-6" data-testid="reset-password-dialog">
            <h2 className="font-bold mb-2">Reset Kata Sandi</h2>
            <p className="text-xs text-slate-500 mb-5">
              Atur kata sandi baru untuk <strong>{resetUser.name}</strong> (@{resetUser.username}). Sampaikan kata sandi baru ini kepada pengguna secara langsung.
            </p>
            <label className={labelCls}>Kata Sandi Baru (min. 6 karakter)</label>
            <input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid="reset-pw-input" className={inputCls} />
            <div className="flex gap-3 mt-5">
              <button onClick={() => setResetUser(null)} data-testid="cancel-reset-pw" className="flex-1 px-6 py-3 border border-slate-300 text-slate-600 font-bold text-sm rounded-md hover:bg-slate-50 transition-colors">
                Batal
              </button>
              <button
                onClick={resetUserPassword}
                disabled={resetPw.length < 6}
                data-testid="save-reset-pw"
                className="flex-1 px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50"
              >
                Simpan Kata Sandi
              </button>
            </div>
          </div>
        </div>
      )}
      {newUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setNewUser(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-testid="account-dialog">
            <h2 className="font-bold mb-5">Buat Akun Baru</h2>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Jenis Akun</label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ v: "customer", l: "Pelanggan" }, { v: "owner", l: "Pemilik" }].map((r) => (
                    <button
                      key={r.v}
                      onClick={() => setNewUser({ ...newUser, role: r.v })}
                      data-testid={`acc-role-${r.v}`}
                      className={`p-3 rounded-md border text-sm font-bold transition-colors ${newUser.role === r.v ? "border-[#C9A227] bg-amber-50 text-[#0B1F3A]" : "border-slate-300 text-slate-500"}`}
                    >
                      {r.l}
                    </button>
                  ))}
                </div>
                {newUser.role === "owner" && (
                  <p className="text-xs text-amber-600 mt-2">Akun Pemilik memiliki akses penuh ke seluruh sistem pembukuan &amp; pengaturan.</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Nama Lengkap *</label>
                <input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="acc-name" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Nama Pengguna *</label>
                  <input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} data-testid="acc-username" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>No. Telepon *</label>
                  <input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} data-testid="acc-phone" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Email (opsional)</label>
                <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="acc-email" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Kata Sandi (min. 6 karakter) *</label>
                <input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="acc-password" className={inputCls} />
              </div>
              {newUser.role === "owner" && (
                <div>
                  <label className={labelCls}>Kode Akses Pemilik (6 digit) *</label>
                  <input type="password" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={newUser.access_code} onChange={(e) => setNewUser({ ...newUser, access_code: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid="acc-access-code" className={inputCls} />
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setNewUser(null)} data-testid="cancel-account" className="flex-1 px-6 py-3 border border-slate-300 text-slate-600 font-bold text-sm rounded-md hover:bg-slate-50 transition-colors">
                  Batal
                </button>
                <button
                  onClick={createUser}
                  disabled={!newUser.name || !newUser.username || !newUser.phone || !newUser.password || (newUser.role === "owner" && newUser.access_code.length !== 6)}
                  data-testid="save-account"
                  className="flex-1 px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50"
                >
                  Buat Akun
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
